/**
 * BPMN 2.0 XML Export Utility
 *
 * Generates standards-compliant BPMN 2.0 XML files compatible with:
 * - bpmn.io (bpmn-js)
 * - Camunda Modeler
 * - Flowable
 * - Other BPMN 2.0 compliant tools
 *
 * Based on OMG BPMN 2.0 Specification
 */

import { ExtendedProcessStep, DecisionPaths, SequenceFlow } from './processCategorization';

// XML Namespaces for BPMN 2.0
const NAMESPACES = {
  bpmn: 'http://www.omg.org/spec/BPMN/20100524/MODEL',
  bpmndi: 'http://www.omg.org/spec/BPMN/20100524/DI',
  dc: 'http://www.omg.org/spec/DD/20100524/DC',
  di: 'http://www.omg.org/spec/DD/20100524/DI',
  xsi: 'http://www.w3.org/2001/XMLSchema-instance',
  targetNamespace: 'http://bpmn.io/schema/bpmn'
};


class BPMNIDGenerator {
  private usedIds: Set<string> = new Set();
  private elementRegistry: Map<string, { type: string; name: string | null }> = new Map();
  private originalToNewIdMap: Map<string, string> = new Map();

  /**
   * Generate UUID v4 using Web Crypto API (browser-native)
   */
  private generateUUID(): string {
    // Use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback: Manual UUID v4 generation using crypto.getRandomValues
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set version (4) and variant (RFC 4122)
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant RFC 4122

    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  /**
   * Generate unique ID for BPMN element
   * Format: {elementType}_{uuid}
   */
  generateId(elementType: string): string {
    let uniqueId: string;
    let attempts = 0;
    const maxAttempts = 100;

    do {
      const uuid = this.generateUUID();
      uniqueId = `${elementType}_${uuid}`;
      attempts++;

      if (attempts >= maxAttempts) {
        throw new Error(`Failed to generate unique ID after ${maxAttempts} attempts`);
      }
    } while (this.usedIds.has(uniqueId));

    this.usedIds.add(uniqueId);
    this.elementRegistry.set(uniqueId, { type: elementType, name: null });
    return uniqueId;
  }

  /**
   * Get or create a unique ID for an original step ID
   * Maintains mapping to ensure consistent IDs throughout export
   */
  getOrCreateId(originalId: string, elementType: string): string {
    // Check if we already have a mapping for this original ID
    if (this.originalToNewIdMap.has(originalId)) {
      return this.originalToNewIdMap.get(originalId)!;
    }

    // Check if originalId is already a valid UUID format (task_uuid pattern)
    const uuidPattern = /^[a-z]+_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(originalId) && !this.usedIds.has(originalId)) {
      // Original ID is already a valid UUID format, use it directly
      this.usedIds.add(originalId);
      this.elementRegistry.set(originalId, { type: elementType, name: null });
      this.originalToNewIdMap.set(originalId, originalId);
      return originalId;
    }

    // Generate a new unique ID
    const newId = this.generateId(elementType);
    this.originalToNewIdMap.set(originalId, newId);
    return newId;
  }

  /**
   * Generate DI element ID (adds _di suffix)
   */
  generateDiId(baseId: string): string {
    const diId = `${baseId}_di`;
    this.usedIds.add(diId);
    return diId;
  }

  /**
   * Register an existing ID (e.g., from backend)
   */
  registerExistingId(id: string, elementType: string = 'unknown'): void {
    this.usedIds.add(id);
    this.elementRegistry.set(id, { type: elementType, name: null });
  }

  /**
   * Check if an ID is unique
   */
  isUnique(id: string): boolean {
    return !this.usedIds.has(id);
  }

  validateXMLUniqueness(xmlString: string): {
    valid: boolean;
    duplicates: string[];
    totalIds: number;
    uniqueIds: number;
  } {
    const idPattern = /id="([^"]+)"/g;
    const allIds: string[] = [];
    let match;

    while ((match = idPattern.exec(xmlString)) !== null) {
      allIds.push(match[1]);
    }

    const idCounts: Record<string, number> = {};
    const duplicates: string[] = [];

    for (const id of allIds) {
      idCounts[id] = (idCounts[id] || 0) + 1;
      if (idCounts[id] === 2) {
        duplicates.push(id);
      }
    }

    if (duplicates.length > 0) {
      return {
        valid: false,
        duplicates: duplicates,
        totalIds: allIds.length,
        uniqueIds: Object.keys(idCounts).length
      };
    }

    return {
      valid: true,
      duplicates: [],
      totalIds: allIds.length,
      uniqueIds: allIds.length
    };
  }

  /**
   * Get statistics about generated IDs
   */
  getStats(): {
    totalGenerated: number;
    elementTypes: Record<string, number>;
  } {
    const counts: Record<string, number> = {};
    this.elementRegistry.forEach((value) => {
      counts[value.type] = (counts[value.type] || 0) + 1;
    });
    return {
      totalGenerated: this.usedIds.size,
      elementTypes: counts
    };
  }

  /**
   * Reset the generator (for new export session)
   */
  reset(): void {
    this.usedIds.clear();
    this.elementRegistry.clear();
    this.originalToNewIdMap.clear();
  }
}

// Create a module-level ID generator instance
let idGenerator: BPMNIDGenerator;

const LAYOUT = {
  stepWidth: 220,
  stepHeight: 100,
  gatewaySize: 60,
  eventSize: 45,
  horizontalSpacing: 140,
  verticalSpacing: 150,
  laneHeight: 200,
  startX: 160,
  startY: 50,
  labelHeight: 25,
  laneHeaderWidth: 60
};

interface BpmnElement {
  id: string;
  type: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  incoming?: string[];
  outgoing?: string[];
  laneId?: string;
  defaultFlowId?: string;
}

interface BpmnConnection {
  id: string;
  sourceRef: string;
  targetRef: string;
  name?: string;
  conditionExpression?: string;
  waypoints: { x: number; y: number }[];
  isDefault?: boolean;
}

interface BpmnLane {
  id: string;
  name: string;
  flowNodeRefs: string[];
  y: number;
  height: number;
}

/**
 * Escape XML special characters
 */
function escapeXml(str: string): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Extract short step title from full step name
 * "Step 1 - Pre-Screening Initiation: Partner Relationship Managers..." -> "Step 1 - Pre-Screening Initiation"
 */
function extractShortStepName(fullText: string): string {
  if (!fullText) return '';

  // Match "Step X - Title:" pattern and extract just "Step X - Title"
  const stepPattern = /^(Step\s+\d+[A-Z]?\s*-\s*[^:]+):/i;
  const match = stepPattern.exec(fullText);
  if (match) {
    return match[1].trim();
  }

  // Check if it starts with "Step X -" pattern
  const stepNoColonPattern = /^(Step\s+\d+[A-Z]?\s*-\s*[^:]{3,40})/i;
  const match2 = stepNoColonPattern.exec(fullText);
  if (match2) {
    return match2[1].trim();
  }

  // For Start/End events
  if (fullText.toLowerCase().startsWith('start') || fullText.toLowerCase().startsWith('end')) {
    const colonIndex = fullText.indexOf(':');
    if (colonIndex > 0 && colonIndex < 50) {
      return fullText.substring(0, colonIndex).trim();
    }
  }

  // Fallback: truncate if too long
  if (fullText.length > 40) {
    return fullText.substring(0, 37) + '...';
  }

  return fullText;
}

function generateId(prefix: string, _index?: number | string): string {
  return idGenerator.generateId(prefix);
}

/**
 * Map process step type to BPMN element type
 */
function getBpmnElementType(step: ExtendedProcessStep): string {
  const actionType = (step.action_type || '').toLowerCase();
  const bpmnType = (step as any).bpmn_type;
  const eventType = (step as any).event_type;
  const taskType = (step as any).task_type;
  const gatewayType = (step as any).gateway_type;
  const stepName = (step.name || '').toLowerCase();

  // Check for events
  if (bpmnType === 'event' || actionType === 'start' || actionType === 'end' ||
      stepName.startsWith('start') || stepName.startsWith('end')) {
    if (eventType === 'startEvent' || actionType === 'start' || stepName.startsWith('start')) {
      return 'bpmn:startEvent';
    }
    if (eventType === 'endEvent' || actionType === 'end' || stepName.startsWith('end')) {
      return 'bpmn:endEvent';
    }
    if (eventType === 'timerEvent') return 'bpmn:intermediateCatchEvent';
    if (eventType === 'messageEvent') return 'bpmn:intermediateCatchEvent';
    return 'bpmn:intermediateThrowEvent';
  }

  // Check for gateways
  if (bpmnType === 'gateway' || actionType === 'decision' || actionType === 'split' ||
      actionType === 'merge' || actionType === 'gateway' || step.type === 'decision' ||
      step.type === 'split' || step.type === 'merge') {
    if (gatewayType === 'parallel' || actionType === 'split' || actionType === 'merge') {
      return 'bpmn:parallelGateway';
    }
    if (gatewayType === 'inclusive') {
      return 'bpmn:inclusiveGateway';
    }
    return 'bpmn:exclusiveGateway';
  }

  // Tasks
  if (taskType === 'serviceTask') return 'bpmn:serviceTask';
  if (taskType === 'sendTask') return 'bpmn:sendTask';
  if (taskType === 'receiveTask') return 'bpmn:receiveTask';
  if (taskType === 'manualTask') return 'bpmn:manualTask';
  if (taskType === 'scriptTask') return 'bpmn:scriptTask';

  return 'bpmn:userTask';
}

/**
 * Calculate element dimensions based on type
 */
function getElementDimensions(elementType: string): { width: number; height: number } {
  if (elementType.includes('Event')) {
    return { width: LAYOUT.eventSize, height: LAYOUT.eventSize };
  }
  if (elementType.includes('Gateway')) {
    return { width: LAYOUT.gatewaySize, height: LAYOUT.gatewaySize };
  }
  return { width: LAYOUT.stepWidth, height: LAYOUT.stepHeight };
}

/**
 * Calculate position for BPMN element
 */
function calculatePosition(step: ExtendedProcessStep, swimlanes: string[], elementType: string): { x: number; y: number } {
  const dims = getElementDimensions(elementType);
  const laneIndex = swimlanes.indexOf(step.swimlane) || 0;

  const x = LAYOUT.startX + (step.position.x * (LAYOUT.stepWidth + LAYOUT.horizontalSpacing));
  const y = LAYOUT.startY + (laneIndex * LAYOUT.laneHeight) + (LAYOUT.laneHeight / 2) - (dims.height / 2);

  return { x, y };
}

function generateSequenceFlows(
  steps: ExtendedProcessStep[],
  elements: Map<string, BpmnElement>
): BpmnConnection[] {
  const flows: BpmnConnection[] = [];
  // Track connections by source-target pair to prevent duplicates
  const processedConnections = new Set<string>();
  const sourceFlowCount = new Map<string, number>();

  // Helper to create unique connection key
  const getConnectionKey = (sourceId: string, targetId: string) => `${sourceId}__to__${targetId}`;

  steps.forEach((step, index) => {
    const sourceElement = elements.get(step.id);
    if (!sourceElement) return;

    if (sourceElement.type.includes('endEvent')) return;

    const decisionPaths = step.decisionPaths || step.decision_paths;
    const isDecision = sourceElement.type.includes('Gateway');

    const getFlowIndex = (sourceId: string) => {
      const count = sourceFlowCount.get(sourceId) || 0;
      sourceFlowCount.set(sourceId, count + 1);
      return count;
    };

    if (decisionPaths && isDecision) {
      let defaultFlowId: string | null = null;
      const noPathDetails = decisionPaths.no_path_details;

      // Skip yes/no paths if parallel_tracks exists (they handle the branching)
      const hasParallelTracks = decisionPaths.parallel_tracks &&
        (decisionPaths.parallel_tracks.track_A?.next_steps?.length ||
         decisionPaths.parallel_tracks.track_B?.next_steps?.length);

      if (decisionPaths.no && !hasParallelTracks) {
        const noTargetId = findTargetStepId(decisionPaths.no, steps);
        const targetElement = noTargetId ? elements.get(noTargetId) : null;

        if (targetElement) {
          const connKey = getConnectionKey(sourceElement.id, targetElement.id);
          if (!processedConnections.has(connKey)) {
            processedConnections.add(connKey);
            const flowId = generateId('Flow');
            const flowIdx = getFlowIndex(step.id);
            defaultFlowId = flowId;
            flows.push({
              id: flowId,
              sourceRef: sourceElement.id,
              targetRef: targetElement.id,
              name: decisionPaths.no_condition || 'No',
              isDefault: true,
              waypoints: calculateWaypoints(sourceElement, targetElement, 'no', flowIdx)
            });
          }
        }

        if (noPathDetails?.steps?.length && noPathDetails.convergence) {
          const lastNoStepRef = noPathDetails.steps[noPathDetails.steps.length - 1];
          const lastNoStepId = findTargetStepId(lastNoStepRef, steps);
          const lastNoElement = lastNoStepId ? elements.get(lastNoStepId) : null;
          const noConvergenceId = findTargetStepId(noPathDetails.convergence, steps);
          const noConvergenceElement = noConvergenceId ? elements.get(noConvergenceId) : null;

          if (lastNoElement && noConvergenceElement && lastNoStepId !== noConvergenceId) {
            const connKey = getConnectionKey(lastNoElement.id, noConvergenceElement.id);
            if (!processedConnections.has(connKey)) {
              processedConnections.add(connKey);
              const flowId = generateId('Flow');
              const flowIdx = getFlowIndex(lastNoStepId || '');
              flows.push({
                id: flowId,
                sourceRef: lastNoElement.id,
                targetRef: noConvergenceElement.id,
                waypoints: calculateWaypoints(lastNoElement, noConvergenceElement, 'no', flowIdx)
              });
            }
          }
        }
      }

      if (decisionPaths.yes && !hasParallelTracks) {
        const yesTargetId = findTargetStepId(decisionPaths.yes, steps);
        const targetElement = yesTargetId ? elements.get(yesTargetId) : null;

        if (targetElement) {
          const connKey = getConnectionKey(sourceElement.id, targetElement.id);
          if (!processedConnections.has(connKey)) {
            processedConnections.add(connKey);
            const flowId = generateId('Flow');
            const flowIdx = getFlowIndex(step.id);
            flows.push({
              id: flowId,
              sourceRef: sourceElement.id,
              targetRef: targetElement.id,
              name: decisionPaths.yes_condition || 'Yes',
              conditionExpression: `\${${decisionPaths.yes_condition?.toLowerCase().replace(/\s+/g, '_') || 'approved'} == true}`,
              waypoints: calculateWaypoints(sourceElement, targetElement, 'yes', flowIdx)
            });
          }
        }
      }

      if (defaultFlowId) {
        sourceElement.defaultFlowId = defaultFlowId;
      }

      if (decisionPaths.parallel_tracks) {
        const trackA = decisionPaths.parallel_tracks.track_A;
        const trackB = decisionPaths.parallel_tracks.track_B;
        const convergenceRef = trackA?.convergence || trackB?.convergence;

        if (trackA?.next_steps?.length) {
          const firstTrackAStep = findTargetStepId(trackA.next_steps[0], steps);
          const targetElement = firstTrackAStep ? elements.get(firstTrackAStep) : null;
          if (targetElement) {
            const connKey = getConnectionKey(sourceElement.id, targetElement.id);
            if (!processedConnections.has(connKey)) {
              processedConnections.add(connKey);
              const flowId = generateId('Flow');
              const flowIdx = getFlowIndex(step.id);
              flows.push({
                id: flowId,
                sourceRef: sourceElement.id,
                targetRef: targetElement.id,
                name: trackA.name || 'Track A',
                waypoints: calculateWaypoints(sourceElement, targetElement, 'yes', flowIdx)
              });
            }
          }

          if (convergenceRef && trackA.next_steps.length > 0) {
            const lastTrackAStepRef = trackA.next_steps[trackA.next_steps.length - 1];
            const lastTrackAStepId = findTargetStepId(lastTrackAStepRef, steps);
            const lastTrackAElement = lastTrackAStepId ? elements.get(lastTrackAStepId) : null;
            const mergeTargetId = findTargetStepId(convergenceRef, steps);
            const mergeElement = mergeTargetId ? elements.get(mergeTargetId) : null;

            if (lastTrackAElement && mergeElement) {
              const connKey = getConnectionKey(lastTrackAElement.id, mergeElement.id);
              if (!processedConnections.has(connKey)) {
                processedConnections.add(connKey);
                const flowId = generateId('Flow');
                const flowIdx = getFlowIndex(lastTrackAStepId || '');
                flows.push({
                  id: flowId,
                  sourceRef: lastTrackAElement.id,
                  targetRef: mergeElement.id,
                  waypoints: calculateWaypoints(lastTrackAElement, mergeElement, 'yes', flowIdx)
                });
              }
            }
          }
        }

        if (trackB?.next_steps?.length) {
          const firstTrackBStep = findTargetStepId(trackB.next_steps[0], steps);
          const targetElement = firstTrackBStep ? elements.get(firstTrackBStep) : null;
          if (targetElement) {
            const connKey = getConnectionKey(sourceElement.id, targetElement.id);
            if (!processedConnections.has(connKey)) {
              processedConnections.add(connKey);
              const flowId = generateId('Flow');
              const flowIdx = getFlowIndex(step.id);
              flows.push({
                id: flowId,
                sourceRef: sourceElement.id,
                targetRef: targetElement.id,
                name: trackB.name || 'Track B',
                waypoints: calculateWaypoints(sourceElement, targetElement, 'no', flowIdx)
              });
            }
          }

          if (convergenceRef && trackB.next_steps.length > 0) {
            const lastTrackBStepRef = trackB.next_steps[trackB.next_steps.length - 1];
            const lastTrackBStepId = findTargetStepId(lastTrackBStepRef, steps);
            const lastTrackBElement = lastTrackBStepId ? elements.get(lastTrackBStepId) : null;
            const mergeTargetId = findTargetStepId(convergenceRef, steps);
            const mergeElement = mergeTargetId ? elements.get(mergeTargetId) : null;

            if (lastTrackBElement && mergeElement) {
              const connKey = getConnectionKey(lastTrackBElement.id, mergeElement.id);
              if (!processedConnections.has(connKey)) {
                processedConnections.add(connKey);
                const flowId = generateId('Flow');
                const flowIdx = getFlowIndex(lastTrackBStepId || '');
                flows.push({
                  id: flowId,
                  sourceRef: lastTrackBElement.id,
                  targetRef: mergeElement.id,
                  waypoints: calculateWaypoints(lastTrackBElement, mergeElement, 'no', flowIdx)
                });
              }
            }
          }
        }
      }
    }

    // Skip connections processing for gateways with parallel tracks (already handled above)
    const hasParallelTracksForConnections = isDecision && decisionPaths?.parallel_tracks &&
      (decisionPaths.parallel_tracks.track_A?.next_steps?.length ||
       decisionPaths.parallel_tracks.track_B?.next_steps?.length);

    if (step.connections && step.connections.length > 0 && !sourceElement.type.includes('endEvent') && !hasParallelTracksForConnections) {
      step.connections.forEach((connId, connIndex) => {
        const targetElement = elements.get(connId);
        if (targetElement) {
          const connKey = getConnectionKey(sourceElement.id, targetElement.id);
          if (!processedConnections.has(connKey)) {
            processedConnections.add(connKey);
            const flowId = generateId('Flow');
            const flowIdx = getFlowIndex(step.id);
            flows.push({
              id: flowId,
              sourceRef: sourceElement.id,
              targetRef: targetElement.id,
              waypoints: calculateWaypoints(sourceElement, targetElement, undefined, flowIdx)
            });
          }
        }
      });
    }

    if ((!step.connections || step.connections.length === 0) &&
        !decisionPaths &&
        !sourceElement.type.includes('endEvent')) {
      const nextStep = steps.find(s => s.position.x === step.position.x + 1 && s.position.y === step.position.y);
      if (nextStep) {
        const targetElement = elements.get(nextStep.id);
        if (targetElement) {
          const flowId = generateId('Flow', `${step.id}_${nextStep.id}_seq`);
          if (!processedConnections.has(flowId)) {
            processedConnections.add(flowId);
            flows.push({
              id: flowId,
              sourceRef: sourceElement.id,
              targetRef: targetElement.id,
              waypoints: calculateWaypoints(sourceElement, targetElement, undefined, 0)
            });
          }
        }
      }
    }
  });

  return flows;
}

function findTargetStepId(ref: string, steps: ExtendedProcessStep[]): string | null {
  if (!ref) return null;

  let target = steps.find(s => s.id === ref);
  if (target) return target.id;

  target = steps.find(s => (s as any).step_number === ref);
  if (target) return target.id;

  const cleanRef = ref.replace(/^L\d+_/, '');
  target = steps.find(s => s.id.includes(cleanRef) || (s as any).step_number === cleanRef);
  if (target) return target.id;

  const refLower = ref.toLowerCase();
  target = steps.find(s => {
    const stepNum = ((s as any).step_number || '').toString().toLowerCase();
    const stepId = (s.id || '').toLowerCase();
    return stepNum === refLower || stepId === refLower || stepId.includes(refLower);
  });
  if (target) return target.id;

  return null;
}

function calculateWaypoints(
  source: BpmnElement,
  target: BpmnElement,
  pathType?: 'yes' | 'no',
  flowIndex: number = 0
): { x: number; y: number }[] {
  const sourceCenterX = source.x + source.width / 2;
  const sourceCenterY = source.y + source.height / 2;
  const targetCenterX = target.x + target.width / 2;
  const targetCenterY = target.y + target.height / 2;

  const waypoints: { x: number; y: number }[] = [];

  const pathOffset = pathType === 'no' ? 50 : (pathType === 'yes' ? 25 : 0);
  const verticalOffset = flowIndex * 15;

  const isGateway = source.type.includes('Gateway');
  const isEndEvent = target.type.includes('endEvent');
  // Use larger tolerance to account for parallel track offsets within same lane
  const isSameLane = Math.abs(sourceCenterY - targetCenterY) < LAYOUT.laneHeight * 0.8;
  const isTargetToRight = target.x > source.x;
  const isTargetBelow = target.y > source.y;

  if (isGateway) {
    if (pathType === 'no') {
      const exitX = sourceCenterX;
      const exitY = source.y + source.height;
      waypoints.push({ x: exitX, y: exitY });

      if (isEndEvent) {
        waypoints.push({ x: exitX, y: targetCenterY });
        waypoints.push({ x: target.x, y: targetCenterY });
      } else if (isTargetToRight) {
        const bendY = exitY + 40 + verticalOffset;
        waypoints.push({ x: exitX, y: bendY });
        waypoints.push({ x: targetCenterX, y: bendY });
        waypoints.push({ x: targetCenterX, y: targetCenterY });
        waypoints.push({ x: target.x, y: targetCenterY });
      } else {
        const bendY = exitY + 40 + verticalOffset;
        waypoints.push({ x: exitX, y: bendY });
        waypoints.push({ x: target.x + target.width + 30, y: bendY });
        waypoints.push({ x: target.x + target.width + 30, y: targetCenterY });
        waypoints.push({ x: target.x + target.width, y: targetCenterY });
      }
    } else {
      const exitX = source.x + source.width;
      const exitY = sourceCenterY;
      waypoints.push({ x: exitX, y: exitY });

      if (isSameLane && isTargetToRight) {
        waypoints.push({ x: target.x, y: targetCenterY });
      } else if (isTargetToRight) {
        const midX = exitX + (target.x - exitX) / 2;
        waypoints.push({ x: midX, y: exitY });
        waypoints.push({ x: midX, y: targetCenterY });
        waypoints.push({ x: target.x, y: targetCenterY });
      } else {
        const bendX = exitX + 50 + pathOffset;
        const bendY = isTargetBelow ?
          Math.max(sourceCenterY, targetCenterY) + 50 + verticalOffset :
          Math.min(sourceCenterY, targetCenterY) - 50 - verticalOffset;
        waypoints.push({ x: bendX, y: exitY });
        waypoints.push({ x: bendX, y: bendY });
        waypoints.push({ x: target.x - 30, y: bendY });
        waypoints.push({ x: target.x - 30, y: targetCenterY });
        waypoints.push({ x: target.x, y: targetCenterY });
      }
    }
  } else {
    const exitX = source.x + source.width;
    const exitY = sourceCenterY;
    waypoints.push({ x: exitX, y: exitY });

    if (isSameLane && isTargetToRight) {
      waypoints.push({ x: target.x, y: targetCenterY });
    } else if (isTargetToRight) {
      const midX = exitX + 50 + pathOffset;
      waypoints.push({ x: midX, y: exitY });
      waypoints.push({ x: midX, y: targetCenterY });
      waypoints.push({ x: target.x, y: targetCenterY });
    } else {
      const bendX = exitX + 40 + pathOffset;
      const bendY = isTargetBelow ?
        source.y + source.height + 40 + verticalOffset :
        source.y - 40 - verticalOffset;
      waypoints.push({ x: bendX, y: exitY });
      waypoints.push({ x: bendX, y: bendY });
      waypoints.push({ x: target.x - 30, y: bendY });
      waypoints.push({ x: target.x - 30, y: targetCenterY });
      waypoints.push({ x: target.x, y: targetCenterY });
    }
  }

  return waypoints;
}

function generateLanes(steps: ExtendedProcessStep[]): BpmnLane[] {
  const laneMap = new Map<string, BpmnLane>();
  let laneIndex = 0;

  steps.forEach((step) => {
    const laneName = step.swimlane || step.assignee || 'Default Lane';

    if (!laneMap.has(laneName)) {
      laneMap.set(laneName, {
        id: generateId('Lane', laneName),
        name: laneName,
        flowNodeRefs: [],
        y: laneIndex * LAYOUT.laneHeight,
        height: LAYOUT.laneHeight
      });
      laneIndex++;
    }

    laneMap.get(laneName)!.flowNodeRefs.push(step.id);
  });

  return Array.from(laneMap.values());
}

function identifySubProcessCandidates(steps: ExtendedProcessStep[]): Map<string, ExtendedProcessStep[]> {
  const subProcessGroups = new Map<string, ExtendedProcessStep[]>();

  steps.forEach(step => {
    const parentRef = (step as any).parent_l3_step || (step as any).parent_step;
    if (parentRef) {
      if (!subProcessGroups.has(parentRef)) {
        subProcessGroups.set(parentRef, []);
      }
      subProcessGroups.get(parentRef)!.push(step);
    }
  });

  const validSubProcesses = new Map<string, ExtendedProcessStep[]>();
  subProcessGroups.forEach((childSteps, parentRef) => {
    if (childSteps.length >= 3) {
      const hasDecision = childSteps.some(s =>
        s.type === 'decision' ||
        (s as any).action_type === 'decision' ||
        (s as any).bpmn_type === 'gateway'
      );
      const hasValidation = childSteps.some(s =>
        (s.name || '').toLowerCase().includes('verify') ||
        (s.name || '').toLowerCase().includes('validate') ||
        (s.name || '').toLowerCase().includes('check')
      );

      if (childSteps.length >= 5 || (hasDecision && hasValidation)) {
        validSubProcesses.set(parentRef, childSteps);
      }
    }
  });

  return validSubProcesses;
}

function generateSubProcessXml(
  subProcessId: string,
  subProcessName: string,
  childSteps: ExtendedProcessStep[],
  elements: Map<string, BpmnElement>,
  flows: BpmnConnection[]
): string {
  const childElementIds = new Set(childSteps.map(s => s.id));

  const internalFlows = flows.filter(f =>
    childElementIds.has(f.sourceRef) || childElementIds.has(f.targetRef)
  );

  const subStartId = generateId('StartEvent');
  const subEndId = generateId('EndEvent');

  let subProcessContent = `
    <bpmn:subProcess id="${subProcessId}" name="${escapeXml(subProcessName)}">
      <bpmn:startEvent id="${subStartId}" name="Start"/>`;

  const sortedSteps = [...childSteps].sort((a, b) => {
    const aIdx = (a as any).step_index ?? (a as any).parent_l3_index ?? 0;
    const bIdx = (b as any).step_index ?? (b as any).parent_l3_index ?? 0;
    return aIdx - bIdx;
  });

  sortedSteps.forEach((step) => {
    const element = elements.get(step.id);
    if (element) {
      const elementType = element.type.replace('bpmn:', '');
      const defaultAttr = element.defaultFlowId ? ` default="${element.defaultFlowId}"` : '';

      subProcessContent += `
      <bpmn:${elementType} id="${element.id}" name="${escapeXml(element.name)}"${defaultAttr}/>`;
    }
  });

  if (sortedSteps.length > 0) {
    const firstStep = sortedSteps[0];
    const firstElement = elements.get(firstStep.id);
    if (firstElement) {
      const startFlowId = generateId('Flow');
      subProcessContent += `
      <bpmn:sequenceFlow id="${startFlowId}" sourceRef="${subStartId}" targetRef="${firstElement.id}"/>`;
    }
  }

  internalFlows.forEach(flow => {
    const nameAttr = flow.name ? ` name="${escapeXml(flow.name)}"` : '';
    let conditionXml = '';
    if (flow.conditionExpression && !flow.isDefault) {
      conditionXml = `
        <bpmn:conditionExpression xsi:type="bpmn:tFormalExpression">${escapeXml(flow.conditionExpression)}</bpmn:conditionExpression>`;
    }
    subProcessContent += `
      <bpmn:sequenceFlow id="${flow.id}" sourceRef="${flow.sourceRef}" targetRef="${flow.targetRef}"${nameAttr}>${conditionXml}
      </bpmn:sequenceFlow>`;
  });

  const lastStep = sortedSteps[sortedSteps.length - 1];
  const lastElement = lastStep ? elements.get(lastStep.id) : null;

  const stepsWithOutgoing = sortedSteps.filter(s => {
    const el = elements.get(s.id);
    return el && (!el.outgoing || el.outgoing.length === 0);
  });

  stepsWithOutgoing.forEach(step => {
    const element = elements.get(step.id);
    if (element && !element.type.includes('endEvent')) {
      const endFlowId = generateId('Flow');
      subProcessContent += `
      <bpmn:sequenceFlow id="${endFlowId}" sourceRef="${element.id}" targetRef="${subEndId}"/>`;
    }
  });

  if (stepsWithOutgoing.length === 0 && lastElement && !lastElement.type.includes('endEvent')) {
    const endFlowId = generateId('Flow');
    subProcessContent += `
      <bpmn:sequenceFlow id="${endFlowId}" sourceRef="${lastElement.id}" targetRef="${subEndId}"/>`;
  }

  subProcessContent += `
      <bpmn:endEvent id="${subEndId}" name="End"/>
    </bpmn:subProcess>`;

  return subProcessContent;
}

function generateSubProcessShapeXml(
  subProcessId: string,
  x: number,
  y: number,
  width: number,
  height: number
): string {
  return `
      <bpmndi:BPMNShape id="${subProcessId}_di" bpmnElement="${subProcessId}" isExpanded="true">
        <dc:Bounds x="${Math.round(x)}" y="${Math.round(y)}" width="${width}" height="${height}" />
      </bpmndi:BPMNShape>`;
}

function isHighLevelTask(stepName: string): boolean {
  const highLevelVerbs = [
    'handle', 'process', 'manage', 'complete', 'perform',
    'execute', 'conduct', 'facilitate', 'coordinate', 'oversee'
  ];
  const nameLower = stepName.toLowerCase();
  return highLevelVerbs.some(verb => nameLower.startsWith(verb + ' ') || nameLower.includes(' ' + verb + ' '));
}

function getHighLevelTaskExpansionHint(stepName: string): string {
  const nameLower = stepName.toLowerCase();

  if (nameLower.includes('handle') || nameLower.includes('process')) {
    return 'Suggested expansion: 1) Verify eligibility 2) Select method/type 3) Execute action 4) Confirm completion 5) Update records';
  }
  if (nameLower.includes('manage')) {
    return 'Suggested expansion: 1) Assess current state 2) Plan action 3) Execute changes 4) Monitor outcome 5) Document results';
  }
  if (nameLower.includes('complete') || nameLower.includes('perform')) {
    return 'Suggested expansion: 1) Review prerequisites 2) Gather materials 3) Execute task 4) Quality check 5) Finalize';
  }
  if (nameLower.includes('execute')) {
    return 'Suggested expansion: 1) Check preconditions 2) Run operation 3) Monitor progress 4) Verify success 5) Log completion';
  }
  if (nameLower.includes('conduct') || nameLower.includes('facilitate')) {
    return 'Suggested expansion: 1) Schedule/plan 2) Prepare materials 3) Execute 4) Collect feedback 5) Document outcomes';
  }

  return 'Consider breaking this task into 3-5 atomic steps for better process clarity';
}

function generateDefinitionsXml(processId: string, processName: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions
    xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
    xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
    xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
    xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    id="Definitions_${processId}"
    targetNamespace="http://bpmn.io/schema/bpmn"
    exporter="JARVIS RCSA Process Map Exporter"
    exporterVersion="1.0">`;
}

function generateElementXml(element: BpmnElement, step: ExtendedProcessStep): string {
  const elementType = element.type.replace('bpmn:', '');
  const documentation = step.description || step.detailed_description || step.hybrid_description || '';
  const incoming = element.incoming?.map(i => `<bpmn:incoming>${i}</bpmn:incoming>`).join('\n      ') || '';
  const outgoing = element.outgoing?.map(o => `<bpmn:outgoing>${o}</bpmn:outgoing>`).join('\n      ') || '';

  let additionalContent = '';

  if (documentation) {
    additionalContent += `
      <bpmn:documentation>${escapeXml(documentation)}</bpmn:documentation>`;
  }

  if ((step as any).timer_definition && element.type.includes('Event')) {
    additionalContent += `
      <bpmn:timerEventDefinition>
        <bpmn:timeDuration>${escapeXml((step as any).timer_definition)}</bpmn:timeDuration>
      </bpmn:timerEventDefinition>`;
  }

  if (element.type.includes('startEvent') && (step as any).event_definition) {
    const eventDef = (step as any).event_definition;
    if (eventDef === 'messageEventDefinition') {
      const msgRef = (step as any).message_ref || 'process_start';
      additionalContent += `
      <bpmn:messageEventDefinition messageRef="${escapeXml(msgRef)}"/>`;
    } else if (eventDef === 'timerEventDefinition' && !(step as any).timer_definition) {
      additionalContent += `
      <bpmn:timerEventDefinition>
        <bpmn:timeCycle>${escapeXml((step as any).trigger_description || '')}</bpmn:timeCycle>
      </bpmn:timerEventDefinition>`;
    } else if (eventDef === 'conditionalEventDefinition') {
      additionalContent += `
      <bpmn:conditionalEventDefinition>
        <bpmn:condition>${escapeXml((step as any).condition_expression || (step as any).trigger_description || '')}</bpmn:condition>
      </bpmn:conditionalEventDefinition>`;
    }
  }

  if (element.type.includes('Gateway') && step.decision_criteria) {
    additionalContent += `
      <bpmn:documentation>Decision Criteria: ${escapeXml(step.decision_criteria)}</bpmn:documentation>`;
  }

  const stepName = step.name || element.name || '';
  if (element.type.includes('Task') && isHighLevelTask(stepName)) {
    const hasSubSteps = (step as any).parent_l3_step || (step as any).substeps?.length > 0;
    if (!hasSubSteps) {
      const hint = getHighLevelTaskExpansionHint(stepName);
      additionalContent += `
      <bpmn:documentation>HIGH-LEVEL TASK - ${escapeXml(hint)}</bpmn:documentation>`;
    }
  }

  const defaultAttr = element.defaultFlowId ? ` default="${element.defaultFlowId}"` : '';

  return `
    <bpmn:${elementType} id="${element.id}" name="${escapeXml(element.name)}"${defaultAttr}>${additionalContent}
      ${incoming}
      ${outgoing}
    </bpmn:${elementType}>`;
}

function generateFlowXml(flow: BpmnConnection): string {
  let conditionXml = '';
  // Skip condition expression for default flows (BPMN spec: default flows should not have conditions)
  if (flow.conditionExpression && !flow.isDefault) {
    conditionXml = `
      <bpmn:conditionExpression xsi:type="bpmn:tFormalExpression">${escapeXml(flow.conditionExpression)}</bpmn:conditionExpression>`;
  }

  const nameAttr = flow.name ? ` name="${escapeXml(flow.name)}"` : '';

  return `
    <bpmn:sequenceFlow id="${flow.id}" sourceRef="${flow.sourceRef}" targetRef="${flow.targetRef}"${nameAttr}>${conditionXml}
    </bpmn:sequenceFlow>`;
}

function generateLaneXml(lane: BpmnLane): string {
  const flowNodeRefs = lane.flowNodeRefs.map(ref => `
        <bpmn:flowNodeRef>${ref}</bpmn:flowNodeRef>`).join('');

  return `
      <bpmn:lane id="${lane.id}" name="${escapeXml(lane.name)}">${flowNodeRefs}
      </bpmn:lane>`;
}

function generateShapeXml(element: BpmnElement): string {
  const isExpanded = element.type.includes('SubProcess') ? ' isExpanded="true"' : '';
  const isMarkerVisible = element.type.includes('Gateway') ? ' isMarkerVisible="true"' : '';

  return `
      <bpmndi:BPMNShape id="${element.id}_di" bpmnElement="${element.id}"${isExpanded}${isMarkerVisible}>
        <dc:Bounds x="${Math.round(element.x)}" y="${Math.round(element.y)}" width="${element.width}" height="${element.height}" />
        <bpmndi:BPMNLabel>
          <dc:Bounds x="${Math.round(element.x)}" y="${Math.round(element.y + element.height + 5)}" width="${element.width}" height="${LAYOUT.labelHeight}" />
        </bpmndi:BPMNLabel>
      </bpmndi:BPMNShape>`;
}

function generateEdgeXml(flow: BpmnConnection): string {
  const waypoints = flow.waypoints.map(wp =>
    `<di:waypoint x="${Math.round(wp.x)}" y="${Math.round(wp.y)}" />`
  ).join('\n        ');

  let labelXml = '';
  if (flow.name) {
    const midPoint = flow.waypoints[Math.floor(flow.waypoints.length / 2)];
    labelXml = `
        <bpmndi:BPMNLabel>
          <dc:Bounds x="${Math.round(midPoint.x - 30)}" y="${Math.round(midPoint.y - 20)}" width="60" height="20" />
        </bpmndi:BPMNLabel>`;
  }

  return `
      <bpmndi:BPMNEdge id="${flow.id}_di" bpmnElement="${flow.id}">
        ${waypoints}${labelXml}
      </bpmndi:BPMNEdge>`;
}

function generateLaneShapeXml(lane: BpmnLane, poolWidth: number, poolStartX: number): string {
  return `
      <bpmndi:BPMNShape id="${lane.id}_di" bpmnElement="${lane.id}" isHorizontal="true">
        <dc:Bounds x="${poolStartX}" y="${LAYOUT.startY + lane.y}" width="${poolWidth}" height="${lane.height}" />
      </bpmndi:BPMNShape>`;
}

function generateParticipantShapeXml(participantId: string, poolStartX: number, poolWidth: number, poolHeight: number): string {
  return `
      <bpmndi:BPMNShape id="${participantId}_di" bpmnElement="${participantId}" isHorizontal="true">
        <dc:Bounds x="${poolStartX}" y="${LAYOUT.startY}" width="${poolWidth}" height="${poolHeight}" />
      </bpmndi:BPMNShape>`;
}

export function exportToBpmn(
  processSteps: ExtendedProcessStep[],
  processName: string = 'Process',
  processId: string = 'Process_1'
): string {
  idGenerator = new BPMNIDGenerator();

  const steps = [...processSteps];

  const endStepUsageMap = new Map<string, Array<{stepNumber: string; stepId: string; index: number; noPath: string}>>();

  steps.forEach((step, index) => {
    const decisionPaths = (step as any).decisionPaths || (step as any).decision_paths;
    const stepType = (step.type || '').toLowerCase();
    const actionType = ((step as any).action_type || '').toLowerCase();
    const bpmnType = (step as any).bpmn_type;
    const gatewayType = (step as any).gateway_type;

    const isDecision = actionType === 'decision' ||
                       stepType === 'decision' ||
                       (bpmnType === 'gateway' && gatewayType === 'exclusive') ||
                       (bpmnType === 'gateway');

    if (isDecision && decisionPaths && decisionPaths.no) {
      const noPath = decisionPaths.no.toString();
      const normalizedNoPath = noPath.toLowerCase();

      const targetEndStep = steps.find(s => {
        const sStepNum = ((s as any).step_number || '').toString().toLowerCase();
        const sId = (s.id || '').toLowerCase();
        const sName = (s.name || '').toLowerCase();
        return sStepNum === normalizedNoPath ||
               sId === normalizedNoPath ||
               sId.includes(normalizedNoPath) ||
               sName.startsWith('end');
      });

      const isEndTarget = normalizedNoPath.startsWith('end') ||
                          normalizedNoPath.includes('rejected') ||
                          normalizedNoPath.includes('terminated') ||
                          normalizedNoPath.includes('nonresponse') ||
                          normalizedNoPath.includes('consequence') ||
                          (targetEndStep && (targetEndStep.type === 'end' ||
                           ((targetEndStep as any).action_type || '').toLowerCase() === 'end' ||
                           ((targetEndStep as any).event_type || '').toLowerCase() === 'endevent'));

      if (isEndTarget) {
        if (!endStepUsageMap.has(noPath)) {
          endStepUsageMap.set(noPath, []);
        }
        endStepUsageMap.get(noPath)!.push({
          stepNumber: (step as any).step_number || '',
          stepId: step.id,
          index,
          noPath: noPath
        });
      }
    }
  });

  endStepUsageMap.forEach((decisions, endStepRef) => {
    if (decisions.length > 1) {
      const originalEndStep = steps.find(s => {
        const sn = ((s as any).step_number || '').toString().toLowerCase();
        const sId = (s.id || '').toLowerCase();
        const refLower = endStepRef.toLowerCase();
        return sn === refLower || sId === refLower || sId.includes(refLower);
      });

      const originalEndName = originalEndStep?.name || 'End - Rejected';
      const endNameParts = originalEndName.split(':')[0].trim();

      decisions.forEach((decision, idx) => {
        if (idx === 0) return;

        const decisionStep = steps[decision.index];
        const decisionPaths = (decisionStep as any).decisionPaths || (decisionStep as any).decision_paths;
        if (!decisionPaths) return;

        const uniqueEndId = `end_step_${decision.stepNumber.replace(/[^a-zA-Z0-9]/g, '_')}_${idx}`;
        const uniqueEndStepNumber = `End-${decision.stepNumber}`;

        const newEndStep: ExtendedProcessStep = {
          id: uniqueEndId,
          name: `${endNameParts} (from Step ${decision.stepNumber})`,
          type: 'end',
          position: {
            x: decisionStep.position.x + 1,
            y: decisionStep.position.y + idx
          },
          swimlane: decisionStep.swimlane || decisionStep.assignee || 'Default',
          assignee: decisionStep.assignee,
          connections: [],
          step_number: uniqueEndStepNumber,
          bpmn_type: 'event',
          event_type: 'endEvent',
          action_type: 'end',
          parent_decision_step: decision.stepNumber
        } as any;

        steps.push(newEndStep);

        decisionPaths.no = uniqueEndStepNumber;
      });
    }
  });

  const cleanProcessId = processId.replace(/[^a-zA-Z0-9_]/g, '_');
  const participantId = `Participant_${cleanProcessId}`;
  const collaborationId = `Collaboration_${cleanProcessId}`;
  const swimlanes = Array.from(new Set(steps.map(s => s.swimlane || s.assignee || 'Default')));
  const lanes = generateLanes(steps);
  const poolStartX = LAYOUT.startX;
  const contentStartX = LAYOUT.startX + LAYOUT.laneHeaderWidth;
  const endStepRepositions = new Map<string, { x: number; y: number; laneName?: string }>();

  const endSteps = steps.filter(s => {
    const stepName = (s.name || '').toLowerCase();
    const stepNumber = ((s as any).step_number || '').toString().toLowerCase();
    return stepName.startsWith('end') || stepNumber.startsWith('end');
  });

  // Track how many End steps are assigned to each parent decision for offset calculation
  const parentDecisionEndCount = new Map<string, number>();

  endSteps.forEach(endStep => {
    const endStepNumber = (endStep as any).step_number || '';
    const endStepId = endStep.id || '';
    const parentDecisionStepRef = (endStep as any).parent_decision_step;

    let parentDecision: ExtendedProcessStep | undefined = undefined;

    // Priority 1: Check if End step has explicit parent_decision_step reference
    if (parentDecisionStepRef) {
      parentDecision = steps.find(s =>
        (s as any).step_number === parentDecisionStepRef ||
        s.id === parentDecisionStepRef ||
        s.id.includes(parentDecisionStepRef)
      );
    }

    // Priority 2: Check if End step number contains a decision step reference (e.g., "End-4" for Step 4)
    if (!parentDecision && endStepNumber) {
      const endStepMatch = endStepNumber.match(/End-(\d+[A-Z]?)/i);
      if (endStepMatch) {
        const decisionRef = endStepMatch[1];
        parentDecision = steps.find(s =>
          (s as any).step_number === decisionRef ||
          s.id.includes(`step_${decisionRef}`) ||
          s.id.includes(`_${decisionRef}`)
        );
      }
    }

    // Priority 3: Find decision step that points to this End step via its 'no' path
    if (!parentDecision) {
      parentDecision = steps.find(s => {
        const decisionPaths = (s as any).decisionPaths || (s as any).decision_paths;
        if (!decisionPaths) return false;

        const noTarget = decisionPaths.no;
        if (noTarget) {
          if (noTarget === endStepNumber) return true;
          if (noTarget === endStepId) return true;
          if (endStepNumber && endStepNumber.toString() === noTarget) return true;
        }

        if (decisionPaths.no_path_details?.convergence === endStepNumber) return true;
        if (decisionPaths.no_path_details?.end_step_name) {
          const endName = (endStep as any).step_name || endStep.name || '';
          if (decisionPaths.no_path_details.end_step_name === endName) return true;
        }

        return false;
      });
    }

    if (parentDecision) {
      const parentId = parentDecision.id;
      const currentCount = parentDecisionEndCount.get(parentId) || 0;
      parentDecisionEndCount.set(parentId, currentCount + 1);

      const parentLaneIndex = lanes.findIndex(l =>
        l.name === (parentDecision!.swimlane || parentDecision!.assignee || 'Default Lane')
      );
      // Each End step gets a unique lane offset to prevent overlap
      const targetLaneIndex = Math.min(parentLaneIndex + 1 + currentCount, lanes.length - 1);
      const targetLane = lanes[targetLaneIndex];

      endStepRepositions.set(endStep.id, {
        x: parentDecision.position.x + 1 + (currentCount * 0.5),
        y: targetLaneIndex,
        laneName: targetLane?.name || 'Default Lane'
      });
    }
  });

  endStepRepositions.forEach((reposition, endStepId) => {
    if (reposition.laneName) {
      lanes.forEach(lane => {
        const idx = lane.flowNodeRefs.indexOf(endStepId);
        if (idx !== -1) {
          lane.flowNodeRefs.splice(idx, 1);
        }
      });

      const targetLane = lanes.find(l => l.name === reposition.laneName);
      if (targetLane && !targetLane.flowNodeRefs.includes(endStepId)) {
        targetLane.flowNodeRefs.push(endStepId);
      }
    }
  });

  const elements = new Map<string, BpmnElement>();

  // Helper function to detect parallel track suffix (A or B)
  const getParallelTrackInfo = (step: ExtendedProcessStep): { isParallel: boolean; track: 'A' | 'B' | null } => {
    const stepNumber = ((step as any).step_number || '').toString();
    const stepName = (step.name || '').toString();

    // Check for A/B suffix in step number (e.g., "21A", "21B", "22A", "22B")
    if (/\d+[Aa]$/i.test(stepNumber) || stepName.includes('Step') && /\d+[Aa]\s*-/i.test(stepName)) {
      return { isParallel: true, track: 'A' };
    }
    if (/\d+[Bb]$/i.test(stepNumber) || stepName.includes('Step') && /\d+[Bb]\s*-/i.test(stepName)) {
      return { isParallel: true, track: 'B' };
    }

    // Also check step ID for track suffix
    const stepId = (step.id || '').toString();
    if (/_TrackA/i.test(stepId) || stepId.endsWith('A') || stepId.includes('_A_')) {
      return { isParallel: true, track: 'A' };
    }
    if (/_TrackB/i.test(stepId) || stepId.endsWith('B') || stepId.includes('_B_')) {
      return { isParallel: true, track: 'B' };
    }

    return { isParallel: false, track: null };
  };

  // Vertical offset for parallel tracks - Track B should be below Track A
  const PARALLEL_TRACK_OFFSET = LAYOUT.laneHeight * 0.6;

  steps.forEach((step, index) => {
    const elementType = getBpmnElementType(step);
    const dims = getElementDimensions(elementType);
    const reposition = endStepRepositions.get(step.id);
    const posX = reposition ? reposition.x : step.position.x;
    const laneName = reposition?.laneName || step.swimlane || step.assignee || 'Default Lane';
    let lane = lanes.find(l => l.name === laneName);
    if (!lane) {
      lane = lanes.find(l => l.flowNodeRefs.includes(step.id));
    }
    if (!lane && lanes.length > 0) {
      lane = lanes[0];
    }
    const laneIndex = lane ? lanes.indexOf(lane) : 0;
    const x = contentStartX + (posX * (LAYOUT.stepWidth + LAYOUT.horizontalSpacing));

    // Calculate base Y position
    let y = LAYOUT.startY + (laneIndex * LAYOUT.laneHeight) + (LAYOUT.laneHeight / 2) - (dims.height / 2);

    // Apply vertical offset for parallel track steps
    const trackInfo = getParallelTrackInfo(step);
    if (trackInfo.isParallel) {
      if (trackInfo.track === 'A') {
        // Track A: move up slightly from center
        y = y - (PARALLEL_TRACK_OFFSET / 2);
      } else if (trackInfo.track === 'B') {
        // Track B: move down from center
        y = y + (PARALLEL_TRACK_OFFSET / 2);
      }
    }

    elements.set(step.id, {
      id: step.id,
      type: elementType,
      name: extractShortStepName(step.name) || `Step ${index + 1}`,
      x: x,
      y: y,
      width: dims.width,
      height: dims.height,
      incoming: [],
      outgoing: [],
      laneId: lane ? lane.id : generateId('Lane', 'Default')
    });
  });

  const flows = generateSequenceFlows(steps, elements);

  flows.forEach(flow => {
    const source = elements.get(flow.sourceRef.replace(/_/g, '-')) ||
                   Array.from(elements.values()).find(e => e.id === flow.sourceRef);
    const target = elements.get(flow.targetRef.replace(/_/g, '-')) ||
                   Array.from(elements.values()).find(e => e.id === flow.targetRef);

    if (source) source.outgoing = [...(source.outgoing || []), flow.id];
    if (target) target.incoming = [...(target.incoming || []), flow.id];
  });

  const allElements = Array.from(elements.values());
  const maxX = Math.max(...allElements.map(e => e.x + e.width)) + 150;
  const poolWidth = maxX - poolStartX;
  // Calculate pool height based on actual element positions to accommodate parallel track offsets
  const maxY = Math.max(...allElements.map(e => e.y + e.height));
  const calculatedHeight = maxY - LAYOUT.startY + 50;
  const laneBasedHeight = lanes.length * LAYOUT.laneHeight;
  const poolHeight = Math.max(calculatedHeight, laneBasedHeight);

  let xml = generateDefinitionsXml(cleanProcessId, processName);

  xml += `
  <bpmn:collaboration id="${collaborationId}">
    <bpmn:participant id="${participantId}" name="" processRef="${cleanProcessId}" />
  </bpmn:collaboration>`;

  xml += `
  <bpmn:process id="${cleanProcessId}" name="${escapeXml(processName)}" isExecutable="true">`;

  if (lanes.length > 0) {
    xml += `
    <bpmn:laneSet id="LaneSet_1">`;
    lanes.forEach(lane => {
      xml += generateLaneXml(lane);
    });
    xml += `
    </bpmn:laneSet>`;
  }

  const subProcessCandidates = identifySubProcessCandidates(steps);
  const elementsInSubProcess = new Set<string>();
  const subProcessElements: Array<{id: string; name: string; childIds: Set<string>; x: number; y: number; width: number; height: number}> = [];

  subProcessCandidates.forEach((childSteps, parentRef) => {
    const subProcessId = generateId('SubProcess');
    const subProcessName = parentRef;
    const childIds = new Set(childSteps.map(s => s.id));

    childSteps.forEach(s => elementsInSubProcess.add(s.id));

    const childElements = childSteps.map(s => elements.get(s.id)).filter(Boolean) as BpmnElement[];
    if (childElements.length > 0) {
      const minX = Math.min(...childElements.map(e => e.x)) - 20;
      const minY = Math.min(...childElements.map(e => e.y)) - 40;
      const maxX = Math.max(...childElements.map(e => e.x + e.width)) + 20;
      const maxY = Math.max(...childElements.map(e => e.y + e.height)) + 40;

      subProcessElements.push({
        id: subProcessId,
        name: subProcessName,
        childIds,
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
      });

      const internalFlows = flows.filter(f => childIds.has(f.sourceRef) && childIds.has(f.targetRef));
      xml += generateSubProcessXml(subProcessId, subProcessName, childSteps, elements, internalFlows);
    }
  });

  allElements.forEach(element => {
    if (elementsInSubProcess.has(element.id)) return;

    const step = steps.find(s => s.id === element.id);
    if (step) {
      xml += generateElementXml(element, step);
    }
  });

  flows.forEach(flow => {
    const sourceInSub = elementsInSubProcess.has(flow.sourceRef);
    const targetInSub = elementsInSubProcess.has(flow.targetRef);
    if (sourceInSub && targetInSub) return;

    xml += generateFlowXml(flow);
  });

  xml += `
  </bpmn:process>`;

  xml += `
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="${collaborationId}">`;

  xml += generateParticipantShapeXml(participantId, poolStartX, poolWidth, poolHeight);

  lanes.forEach(lane => {
    xml += generateLaneShapeXml(lane, poolWidth, poolStartX);
  });

  subProcessElements.forEach(sp => {
    xml += generateSubProcessShapeXml(sp.id, sp.x, sp.y, sp.width, sp.height);
  });

  allElements.forEach(element => {
    xml += generateShapeXml(element);
  });

  flows.forEach(flow => {
    const sourceInSub = elementsInSubProcess.has(flow.sourceRef);
    const targetInSub = elementsInSubProcess.has(flow.targetRef);
    if (sourceInSub && targetInSub) return;

    xml += generateEdgeXml(flow);
  });

  xml += `
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

  const validation = idGenerator.validateXMLUniqueness(xml);
  if (!validation.valid) {
    console.warn('BPMN Export: Duplicate IDs detected', validation.duplicates);
  }

  return xml;
}

export function downloadBpmnFile(
  processSteps: ExtendedProcessStep[],
  processName: string = 'Process',
  processId: string = 'Process_1'
): void {
  const xml = exportToBpmn(processSteps, processName, processId);

  const blob = new Blob([xml], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${processName.replace(/[^a-zA-Z0-9]/g, '_')}.bpmn`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default { exportToBpmn, downloadBpmnFile };
